<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Draggable from 'vuedraggable';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import PipelineContactFilter from './PipelineContactFilter.vue';
import PipelineSwitcher from './PipelineSwitcher.vue';
import PipelineCard from './PipelineCard.vue';
import PipelineMetricsPanel from './PipelineMetricsPanel.vue';
import PipelineOpportunityDialog from './PipelineOpportunityDialog.vue';
import { formatMoney } from './utils/currency';
import { createCardMoveHandler } from './utils/cardMove';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// REATIVO: navegar para outro `:pipelineId` na MESMA rota reaproveita este
// componente (não re-monta), então capturar o id uma vez no setup deixaria todas
// as ações (board/move/update/metrics/reorder) presas ao pipeline antigo. Lemos
// sempre de route.params para que o id acompanhe a navegação. O `watch` abaixo
// recarrega o board e reseta os filtros ao trocar de pipeline.
const pipelineId = computed(() => Number(route.params.pipelineId));
const accountId = route.params.accountId;

const board = ref({ name: '', stages: [], forecast: null });
const loading = ref(true);

// Lista de pipelines da conta p/ o seletor (autocomplete) no cabeçalho.
const accountPipelines = useMapGetter('pipelines/getPipelines');

// --- Filtros do board (inbox + contato) ----------------------------------
const inboxes = useMapGetter('inboxes/getInboxes');
// '' = todas as caixas. SelectMenu trabalha com strings, então os ids das
// inboxes são normalizados para string nas options e de volta para número/''
// no handler antes de chamar a API.
const selectedInboxId = ref('');
// Objeto { id, name } do contato escolhido no autocomplete; null quando vazio.
const selectedContact = ref(null);

const stages = computed(() => board.value.stages);
const canReorderStages = computed(() => stages.value.length > 1);

// --- Cabeçalho de previsão -------------------------------------------------
// O backend já entrega o forecast agregado (total/aberto/ponderado + contagem).
const forecast = computed(() => board.value.forecast || null);
const forecastCards = computed(() => {
  if (!forecast.value) return [];
  return [
    {
      key: 'total',
      icon: 'i-lucide-coins',
      label: t('PIPELINES.BOARD.FORECAST.TOTAL'),
      value: formatMoney(forecast.value.total_value),
      accent: 'text-n-slate-12',
    },
    {
      key: 'open',
      icon: 'i-lucide-circle-dollar-sign',
      label: t('PIPELINES.BOARD.FORECAST.OPEN'),
      value: formatMoney(forecast.value.open_value),
      accent: 'text-n-blue-11',
    },
    {
      key: 'weighted',
      icon: 'i-lucide-trending-up',
      label: t('PIPELINES.BOARD.FORECAST.WEIGHTED'),
      value: formatMoney(forecast.value.weighted_value),
      accent: 'text-n-teal-11',
    },
  ];
});
const forecastCount = computed(() => forecast.value?.count ?? 0);

// Primeiro estágio com cada comportamento — alvos das ações rápidas do card.
const wonStage = computed(
  () => stages.value.find(stage => stage.behavior === 'won') || null
);
const lostStage = computed(
  () => stages.value.find(stage => stage.behavior === 'lost') || null
);

// --- Helpers de cabeçalho de estágio (WIP + acento won/lost) ---------------
const stageCount = stage => stage.count ?? stage.conversations?.length ?? 0;

// Acima do limite WIP => sinal visual (vermelho). Não bloqueia o movimento.
const isOverWipLimit = stage =>
  stage.wip_limit != null && stageCount(stage) > stage.wip_limit;

const stageCountLabel = stage =>
  stage.wip_limit != null
    ? `${stageCount(stage)}/${stage.wip_limit}`
    : `${stageCount(stage)}`;

const STAGE_ACCENTS = {
  won: {
    icon: 'i-lucide-trophy',
    iconClass: 'text-n-teal-10',
    titleClass: 'text-n-teal-11',
    barClass: 'bg-n-teal-9',
  },
  lost: {
    icon: 'i-lucide-x-circle',
    iconClass: 'text-n-slate-10',
    titleClass: 'text-n-slate-11',
    barClass: 'bg-n-ruby-9',
  },
};
const stageAccent = stage => STAGE_ACCENTS[stage.behavior] || null;

// Opções do filtro de caixa de entrada: "Todas as caixas" + uma por inbox.
const inboxOptions = computed(() => [
  { value: '', label: t('PIPELINES.BOARD.FILTERS.INBOX_ALL') },
  ...inboxes.value.map(inbox => ({
    value: String(inbox.id),
    label: inbox.name,
  })),
]);

// Rótulo compacto do botão do filtro: "Caixa: <nome>" (ex.: "Caixa: Todas").
const inboxFilterLabel = computed(() => {
  const selected = inboxOptions.value.find(
    option => option.value === selectedInboxId.value
  );
  return `${t('PIPELINES.BOARD.FILTERS.INBOX_LABEL')}: ${selected?.label ?? t('PIPELINES.BOARD.FILTERS.INBOX_ALL')}`;
});

// `silent`: refresh em segundo plano (sem estado de loading) usado após mover/editar um
// card — o card já mudou de coluna no draggable, então NÃO queremos piscar o board; só
// reconciliar os totais/etiquetas derivados. O load inicial e troca de filtro usam loading.
const loadBoard = async ({ silent = false } = {}) => {
  if (!silent) loading.value = true;
  try {
    board.value = await store.dispatch('pipelines/getBoard', {
      pipelineId: pipelineId.value,
      inboxId: selectedInboxId.value || undefined,
      contactId: selectedContact.value?.id || undefined,
    });
  } finally {
    if (!silent) loading.value = false;
  }
};

// Pré-seleciona o filtro de contato quando o board é aberto a partir do painel
// do contato (botão "Ver no funil" -> ?contact_id=). Resolve o { id, name } via
// store de contatos (action `show` + getter parametrizado `getContact`); se o
// fetch falhar, usa o id cru para o filtro ainda valer.
const preselectContactFromQuery = async () => {
  const contactId = Number(route.query.contact_id);
  if (!contactId) return;
  await store.dispatch('contacts/show', { id: contactId });
  const contact = store.getters['contacts/getContact'](contactId);
  selectedContact.value = {
    id: contactId,
    name: contact?.name || `#${contactId}`,
  };
};

onMounted(async () => {
  if (!inboxes.value.length) {
    await store.dispatch('inboxes/get');
  }
  // Lista de pipelines p/ o seletor — assíncrono, não bloqueia o board (o seletor já
  // mostra o nome atual vindo do board enquanto a lista carrega).
  store.dispatch('pipelines/get');
  await preselectContactFromQuery();
  await loadBoard();
});

// Qualquer mudança de filtro recarrega o board já com os filtros aplicados.
// O SelectMenu emite a string da option; '' = todas as caixas.
const onInboxChange = value => {
  selectedInboxId.value = value;
  loadBoard();
};
const onContactChange = contact => {
  selectedContact.value = contact;
  // Interação manual passa a mandar: tira o ?contact_id da URL (veio do "Ver no funil")
  // p/ não re-aplicar o filtro depois e p/ o usuário conseguir limpar/trocar o contato.
  if (route.query.contact_id) {
    const query = { ...route.query };
    delete query.contact_id;
    router.replace({ name: 'pipeline_board', params: route.params, query });
  }
  loadBoard();
};

// Trocar de pipeline pelo seletor: navega p/ o board do pipeline escolhido (mesma rota,
// muda :pipelineId). O componente é reaproveitado, então o watch abaixo cuida do reload.
const onPipelineSwitch = id => {
  if (Number(id) === pipelineId.value) return;
  router.push({
    name: 'pipeline_board',
    params: { accountId, pipelineId: id },
  });
};

// Trocar :pipelineId na MESMA rota não re-monta o componente — resetamos os filtros e
// recarregamos o board com o id novo (pipelineId é computed de route.params).
watch(
  () => route.params.pipelineId,
  () => {
    selectedContact.value = null;
    selectedInboxId.value = '';
    loadBoard();
  }
);

// --- Clicar no card abre o MODAL de resumo da oportunidade -----------------
// O vuedraggable distingue clique de arraste, então um @click no card só
// dispara em clique simples; o mover-entre-colunas continua intacto. O modal é
// alimentado pelos dados do próprio card (sem novo fetch).
const opportunityDialog = ref(null);
const activeCard = ref(null);
const activeStage = ref(null);

// Normaliza o card single-stage para o shape que o dialog consome.
const activeOpportunity = computed(() => {
  const card = activeCard.value;
  if (!card) return null;
  return {
    display_id: card.display_id,
    contact_name: card.contact_name,
    channel_type: card.channel_type,
    stage_name: activeStage.value?.name || '',
    behavior: activeStage.value?.behavior || 'open',
    probability: activeStage.value?.probability ?? null,
    value: card.value,
    assignee: card.assignee || null,
    priority: card.priority,
    last_activity_at: card.last_activity_at,
    labels: card.label_list || [],
  };
});

const openCardDialog = (card, stage) => {
  activeCard.value = card;
  activeStage.value = stage;
  opportunityDialog.value?.open();
};

// "Ver conversa" do modal: mesma navegação que o card fazia antes (por
// display_id). Fecha o modal antes de navegar.
const openConversation = displayId => {
  opportunityDialog.value?.close();
  router.push({
    name: 'inbox_conversation',
    params: { accountId, conversation_id: displayId },
  });
};

// --- Reordenar COLUNAS (stages) ------------------------------------------
// O Draggable externo usa `v-model` em board.value.stages, então a ordem
// local já reflete o drop. Persistimos com a action otimista; em erro ela
// reverte o state da store — recarregamos o board para refletir o snapshot.
const onStageReorder = async () => {
  const stageIds = stages.value.map(stage => stage.id);
  try {
    await store.dispatch('pipelines/reorderStages', {
      pipelineId: pipelineId.value,
      stageIds,
    });
  } catch (error) {
    useAlert(t('PIPELINES.BOARD.REORDER_ERROR'));
    await loadBoard({ silent: true });
  }
};

// --- Mover CARD entre colunas (re-etiqueta a conversa) --------------------
// Devolve o card à coluna de origem, na posição original, desfazendo o drop.
const revertCardMove = ({ card, toStage, source }) => {
  const destIndex = toStage.conversations.findIndex(
    item => item.id === card.id
  );
  if (destIndex !== -1) toStage.conversations.splice(destIndex, 1);
  if (!source) return;
  const origin = stages.value.find(stage => stage.id === source.stageId);
  if (!origin) return;
  const insertAt = Math.min(
    source.oldIndex ?? origin.conversations.length,
    origin.conversations.length
  );
  origin.conversations.splice(insertAt, 0, source.element);
};

const moveCardBetweenStages = async ({
  card,
  fromStageId,
  toStage,
  source,
}) => {
  try {
    await store.dispatch('pipelines/moveCard', {
      pipelineId: pipelineId.value,
      conversationId: card.id,
      fromStageId,
      toStageId: toStage.id,
    });
    // Move otimista: o card já saiu da origem no draggable, então o usuário vê o
    // resultado NA HORA. O backend é single-stage; reconciliamos com um refresh
    // SILENCIOSO (sem loading/flicker) — só os totais/etiquetas se atualizam em
    // segundo plano, sem o board piscar nem dar sensação de "travou".
    await loadBoard({ silent: true });
  } catch (error) {
    revertCardMove({ card, toStage, source });
    const reason = error?.response?.data?.error;
    const message =
      reason === 'destination_stage_has_no_labels'
        ? t('PIPELINES.BOARD.MOVE_NO_LABELS')
        : t('PIPELINES.BOARD.MOVE_ERROR');
    useAlert(message);
  }
};

// vuedraggable dispara DOIS eventos num move entre colunas (`added` no destino e
// `removed` na origem) e o SortableJS emite o `added` ANTES do `removed` — então a
// origem só é conhecida depois do destino. O helper coalesce os dois lados e chama
// `moveCardBetweenStages` quando o par fecha (ver utils/cardMove.js).
const { onCardChange } = createCardMoveHandler(moveCardBetweenStages);

// --- Ações rápidas do card -------------------------------------------------
// Define o valor do negócio (PATCH update_card). A store já atualiza o card em
// memória; recarregamos para refletir os totais derivados (forecast/coluna).
const onCardSetValue = async ({ card, value }) => {
  try {
    await store.dispatch('pipelines/updateCard', {
      pipelineId: pipelineId.value,
      conversationId: card.id,
      value,
    });
    await loadBoard({ silent: true });
  } catch (error) {
    useAlert(t('PIPELINES.BOARD.CARD.VALUE_ERROR'));
  }
};

// "Marcar Ganho/Perdido" = mover o card para o estágio won/lost (o backend
// resolve a conversa). Reusa o mesmo fluxo de move entre colunas.
const onCardMarkStage = async ({ card, toStage }) => {
  const origin = stages.value.find(stage =>
    stage.conversations.some(conversation => conversation.id === card.id)
  );
  if (!origin || origin.id === toStage.id) return;
  await moveCardBetweenStages({
    card,
    fromStageId: origin.id,
    toStage,
    source: null,
  });
};

// Prioridade da conversa (reusa a action existente do core de conversas).
const onCardSetPriority = async ({ card, priority }) => {
  try {
    await store.dispatch('conversations/assignPriority', {
      conversationId: card.id,
      priority,
    });
    await loadBoard({ silent: true });
  } catch (error) {
    useAlert(t('PIPELINES.BOARD.CARD.PRIORITY_ERROR'));
  }
};

// --- Ações do modal de oportunidade ----------------------------------------
// Reusam os mesmos fluxos do card (definidos acima); o modal só repassa o card
// ativo. Edição de valor e "Marcar Ganho/Perdido" a partir do modal.
const onDialogSetValue = ({ value }) => {
  if (!activeCard.value) return;
  onCardSetValue({ card: activeCard.value, value });
};

const onDialogMarkStage = ({ toStage }) => {
  if (!activeCard.value) return;
  onCardMarkStage({ card: activeCard.value, toStage });
  opportunityDialog.value?.close();
};

// --- Painel de métricas ----------------------------------------------------
const isMetricsOpen = ref(false);
const metrics = ref(null);
const metricsLoading = ref(false);

const openMetrics = async () => {
  isMetricsOpen.value = true;
  metricsLoading.value = true;
  try {
    metrics.value = await store.dispatch('pipelines/fetchMetrics', {
      pipelineId: pipelineId.value,
      inbox_id: selectedInboxId.value || undefined,
      contact_id: selectedContact.value?.id || undefined,
    });
  } catch (error) {
    useAlert(t('PIPELINES.METRICS.ERROR'));
    isMetricsOpen.value = false;
  } finally {
    metricsLoading.value = false;
  }
};

const backToList = () =>
  router.push({
    name: 'pipelines_index',
    params: { accountId },
    query: { manage: '1' },
  });
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <header
      class="flex flex-wrap gap-3 items-center px-6 py-4 border-b border-n-weak"
    >
      <!-- Esquerda: voltar + nome do pipeline -->
      <button
        type="button"
        class="flex gap-1 items-center text-sm text-n-slate-11 hover:text-n-slate-12"
        @click="backToList"
      >
        <span class="size-4 i-lucide-arrow-left" />
        {{ t('PIPELINES.BOARD.BACK') }}
      </button>
      <span class="w-px h-4 bg-n-strong" />
      <!-- Seletor de pipeline (autocomplete): troca de funil sem voltar à lista. -->
      <PipelineSwitcher
        :pipelines="accountPipelines"
        :current-id="pipelineId"
        :current-name="board.name"
        @select="onPipelineSwitch"
      />

      <!-- Botão de métricas (abre o slide-over) -->
      <button
        type="button"
        class="flex gap-1.5 items-center px-3 h-8 text-sm rounded-lg ltr:ml-auto rtl:mr-auto outline outline-1 outline-n-weak text-n-slate-12 hover:bg-n-alpha-1"
        @click="openMetrics"
      >
        <span class="size-4 i-lucide-bar-chart-3 text-n-slate-11" />
        {{ t('PIPELINES.BOARD.METRICS') }}
      </button>

      <!-- Direita: grupo de filtros compacto e alinhado -->
      <div
        class="flex flex-wrap gap-2 items-center"
        role="group"
        :aria-label="t('PIPELINES.BOARD.FILTERS.GROUP_LABEL')"
      >
        <span
          class="hidden gap-1.5 items-center text-sm text-n-slate-11 sm:flex"
        >
          <span class="size-4 i-lucide-list-filter" />
          {{ t('PIPELINES.BOARD.FILTERS.GROUP_LABEL') }}
        </span>
        <!-- Filtro de caixa de entrada (selo + SelectMenu = um único pill) -->
        <div class="flex items-center h-8">
          <span
            class="grid flex-shrink-0 place-items-center h-8 rounded-lg w-8 text-n-slate-11 bg-n-alpha-2 ltr:rounded-r-none rtl:rounded-l-none"
            :title="t('PIPELINES.BOARD.FILTERS.INBOX_LABEL')"
          >
            <span class="size-4 i-lucide-inbox" />
          </span>
          <SelectMenu
            :model-value="selectedInboxId"
            :options="inboxOptions"
            :label="inboxFilterLabel"
            class="[&>button]:ltr:rounded-l-none [&>button]:rtl:rounded-r-none"
            @update:model-value="onInboxChange"
          />
        </div>
        <!-- Filtro de contato -->
        <PipelineContactFilter
          :model-value="selectedContact"
          @update:model-value="onContactChange"
        />
      </div>
    </header>

    <!-- Cabeçalho de previsão: total / em aberto / previsão ponderada -->
    <div
      v-if="!loading && forecast"
      class="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-n-weak bg-n-solid-1"
    >
      <span class="flex items-center gap-1.5 text-xs text-n-slate-11">
        <span class="size-4 i-lucide-briefcase text-n-slate-10" />
        {{ t('PIPELINES.BOARD.FORECAST.COUNT', { count: forecastCount }) }}
      </span>
      <div
        v-for="card in forecastCards"
        :key="card.key"
        class="flex items-center gap-2 px-3 py-1.5 rounded-lg outline outline-1 outline-n-weak bg-n-background"
      >
        <span class="size-4" :class="[card.icon, card.accent]" />
        <span class="flex flex-col leading-tight">
          <span class="text-[10px] uppercase tracking-wide text-n-slate-10">
            {{ card.label }}
          </span>
          <span class="text-sm font-semibold tabular-nums" :class="card.accent">
            {{ card.value }}
          </span>
        </span>
      </div>
    </div>

    <div v-if="loading" class="p-6 text-sm text-n-slate-11">
      {{ t('PIPELINES.BOARD.LOADING') }}
    </div>
    <div v-else-if="!stages.length" class="p-6 text-sm text-n-slate-11">
      {{ t('PIPELINES.BOARD.NO_STAGES') }}
    </div>
    <Draggable
      v-else
      v-model="board.stages"
      :disabled="!canReorderStages"
      :group="{ name: 'pipeline-stages' }"
      item-key="id"
      handle=".pipeline-stage-handle"
      ghost-class="opacity-40"
      class="flex overflow-x-auto flex-grow gap-4 items-start p-6"
      @end="onStageReorder"
    >
      <template #item="{ element: stage }">
        <section
          class="flex overflow-hidden flex-col flex-shrink-0 gap-2 pb-2 rounded-xl outline outline-1 outline-n-weak bg-n-solid-2 w-72"
        >
          <!-- Faixa de acento: verde (won) / vermelho (lost) -->
          <span
            v-if="stageAccent(stage)"
            class="h-1 shrink-0"
            :class="stageAccent(stage).barClass"
          />
          <div
            class="pipeline-stage-handle flex gap-2 items-center px-3 cursor-grab"
            :class="[
              { 'cursor-default': !canReorderStages },
              stageAccent(stage) ? 'pt-2' : 'pt-3',
            ]"
          >
            <span
              v-if="canReorderStages"
              class="flex-shrink-0 size-4 i-lucide-grip-vertical text-n-slate-10"
              :title="t('PIPELINES.BOARD.DRAG_STAGE')"
            />
            <span
              v-if="stageAccent(stage)"
              class="flex-shrink-0 size-4"
              :class="[stageAccent(stage).icon, stageAccent(stage).iconClass]"
            />
            <div class="flex flex-col flex-grow min-w-0">
              <h2
                class="text-sm font-medium truncate"
                :class="stageAccent(stage)?.titleClass || 'text-n-slate-12'"
              >
                {{ stage.name }}
              </h2>
              <!-- Valor total da etapa (soma dos deal_value dos cards). Teal quando > 0. -->
              <span
                class="text-xs font-semibold leading-tight tabular-nums"
                :class="
                  stage.total_value > 0 ? 'text-n-teal-11' : 'text-n-slate-10'
                "
                :title="t('PIPELINES.BOARD.STAGE.TOTAL_VALUE')"
              >
                {{ formatMoney(stage.total_value) }}
              </span>
            </div>
            <span
              v-if="stage.probability != null"
              class="px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
              :title="t('PIPELINES.BOARD.STAGE.PROBABILITY')"
            >
              {{ `${stage.probability}%` }}
            </span>
            <span
              class="px-1.5 py-0.5 text-xs font-medium rounded-md"
              :class="
                isOverWipLimit(stage)
                  ? 'bg-n-ruby-3 text-n-ruby-11'
                  : 'bg-n-alpha-2 text-n-slate-11'
              "
              :title="
                stage.wip_limit != null
                  ? t('PIPELINES.BOARD.STAGE.WIP_TITLE', {
                      limit: stage.wip_limit,
                    })
                  : ''
              "
            >
              {{ stageCountLabel(stage) }}
            </span>
          </div>
          <div v-if="stage.labels.length" class="flex flex-wrap gap-1 px-3">
            <span
              v-for="label in stage.labels"
              :key="label.id"
              class="px-1.5 py-0.5 text-xs text-white rounded-md"
              :style="{ backgroundColor: label.color }"
            >
              {{ label.title }}
            </span>
          </div>
          <Draggable
            :list="stage.conversations"
            :group="{ name: 'pipeline' }"
            item-key="id"
            class="flex flex-col gap-2 px-3 pt-1 min-h-[48px]"
            @change="event => onCardChange(event, stage)"
          >
            <template #item="{ element }">
              <PipelineCard
                :card="element"
                :stage="stage"
                :won-stage="wonStage"
                :lost-stage="lostStage"
                @open="card => openCardDialog(card, stage)"
                @set-value="onCardSetValue"
                @mark-stage="onCardMarkStage"
                @set-priority="onCardSetPriority"
              />
            </template>
            <template #footer>
              <div
                v-if="!stage.conversations.length"
                class="flex items-center justify-center px-3 py-6 text-xs text-center rounded-lg border border-dashed border-n-strong text-n-slate-10"
              >
                {{ t('PIPELINES.BOARD.EMPTY_STAGE') }}
              </div>
            </template>
          </Draggable>
        </section>
      </template>
    </Draggable>

    <PipelineMetricsPanel
      :open="isMetricsOpen"
      :loading="metricsLoading"
      :metrics="metrics"
      @close="isMetricsOpen = false"
    />

    <!-- Modal de resumo da oportunidade (abre ao clicar no card) -->
    <PipelineOpportunityDialog
      ref="opportunityDialog"
      :opportunity="activeOpportunity"
      editable-value
      :won-stage="wonStage"
      :lost-stage="lostStage"
      @view-conversation="openConversation"
      @set-value="onDialogSetValue"
      @mark-stage="onDialogMarkStage"
    />
  </div>
</template>
